import { LOWER_UUID_PATTERN } from "./kernel/kernel-internal.js";
import { FEDERATED_RELEASE_TOKEN_MAX_TTL_SECONDS, SUBMITTER_KIND_CLAIM, machineEnrollResponseSchema, principalPublicJwkSchema } from "./federated-token.js";
import { GRANT_RESOURCE_TYPES, PLATFORM_RESOURCE_ID } from "./grant-principal.js";
import { z } from "zod";
//#region src/machine-identity-admin.ts
/**
* The machine identity admin API of the control plane: the wire shapes of the routes under
* `/v1/admin/machine-principals` in cp-auth-api, and the body of a machine resource grant on the
* existing grant route (plan 2026-10-21-release-on-zion-build, R-042 item 1, R-044 items 1 and 2).
*
* What it is for. Each job of `.zion/workflows/release.yml` federates to its own machine principal.
* A platform administrator makes that principal, its trust policy and its grants in the CP console.
* This module is the one source of those shapes for both sides:
* - cp-auth-api binds the request schemas with `@Body({ schema })` and `@Param({ schema })`, and
*   answers the response shapes (WS-15b);
* - the console pane parses every answer with the response schemas and sends the request shapes
*   (WS-17);
* - the identity MCP server of site-admin-api sends the request shapes and parses every answer
*   (plan 2026-10-28-identity-mcp-tools, I6 and D4). The key shapes, the route form of the trust
*   policy body and the platform registry role shapes are its additions.
*
* The routes, the permission that gates each one, and the schema of each side:
*
* | Route | Permission | Request | Answer |
* |---|---|---|---|
* | `GET /v1/admin/machine-principals` | `cp:machine-principal:read` | none | {@link cpMachinePrincipalListSchema} |
* | `POST /v1/admin/machine-principals` | `cp:machine-principal:create` | {@link createCpMachinePrincipalRequestSchema} | {@link cpMachinePrincipalSchema}, 201 |
* | `GET /v1/admin/machine-principals/:id` | `cp:machine-principal:read` | {@link cpMachinePrincipalParamSchema} | {@link cpMachinePrincipalDetailSchema} |
* | `POST /v1/admin/machine-principals/:id/disable` | `cp:machine-principal:disable` | {@link cpMachinePrincipalParamSchema} | {@link cpMachinePrincipalSchema}, 200 |
* | `GET /v1/admin/machine-principals/:id/trust-policies` | `cp:machine-trust-policy:read` | {@link cpMachinePrincipalParamSchema} | {@link cpMachineTrustPolicyListSchema} |
* | `POST /v1/admin/machine-principals/:id/trust-policies` | `cp:machine-trust-policy:create` | {@link createCpMachineTrustPolicyRequestSchema} | {@link cpMachineTrustPolicySchema}, 201 |
* | `POST /v1/admin/machine-principals/:id/trust-policies/:policyId/disable` | `cp:machine-trust-policy:disable` | {@link cpMachineTrustPolicyParamSchema} | {@link cpMachineTrustPolicySchema}, 200 |
* | `POST /v1/admin/machine-principals/:id/trust-policies/:policyId/token-ttl` | `cp:machine-trust-policy:create` | {@link setCpMachineTrustPolicyTokenTtlRequestSchema} | {@link cpMachineTrustPolicySchema}, 200 |
* | `GET /v1/admin/machine-principals/:id/site-grants` | `cp:machine-site-grant:read` | {@link cpMachinePrincipalParamSchema} | {@link cpMachineSiteGrantListSchema} |
* | `PUT /v1/admin/machine-principals/:id/site-grants/:permission` | `cp:machine-site-grant:write` | {@link cpMachineSiteGrantParamSchema}, no body | {@link cpMachineSiteGrantSchema}, 200 |
* | `DELETE /v1/admin/machine-principals/:id/site-grants/:permission` | `cp:machine-site-grant:write` | {@link cpMachineSiteGrantParamSchema} | no body, 204 |
* | `GET /v1/admin/machine-principals/:id/keys` | `cp:machine-trust-policy:read` | {@link cpMachinePrincipalParamSchema} | {@link cpMachineKeyListSchema} |
* | `POST /v1/admin/machine-principals/:id/keys` | `cp:machine-trust-policy:create` | {@link registerCpMachineKeyRequestSchema} | `machineEnrollResponseSchema`, 201 |
* | `DELETE /v1/admin/machine-principals/:id/keys/:kid` | `cp:machine-trust-policy:disable` | {@link cpMachineKeyParamSchema} | {@link cpMachineKeySchema}, 200 |
* | `POST /v1/admin/machine-principals/:id/platform-roles` | {@link CP_MACHINE_PLATFORM_ROLE_PERMISSION} | {@link grantCpMachinePlatformRoleRequestSchema} | {@link cpMachinePlatformRoleGrantSchema}, 201 |
* | `DELETE /v1/admin/machine-principals/:id/platform-roles/:roleSlug` | {@link CP_MACHINE_PLATFORM_ROLE_PERMISSION} | {@link cpMachinePlatformRoleParamSchema} | no body, 204 |
* | `POST /v1/admin/permissions/grants` | `cp:permission:grant` | {@link createCpMachineResourceGrantRequestSchema} | {@link cpMachineResourceGrantRowSchema}, 201 |
*
* Every request schema and every answer schema is strict: an unknown key at any level is a
* `ZodError`. No schema here carries a `.transform()`, so `createOpenApiDocument()` can render each
* one (CLAUDE.md, zod 4 hard constraint).
*
* Every export is pure: no clock, no I/O, no module state beyond frozen constants.
*/
/**
* The path prefix of the machine identity admin routes on the CP gateway. The gateway sends it to
* cp-auth-api (the `PathPrefix /v1/admin/machine-principals` rule of `httproute.yaml`).
*/
const CP_MACHINE_PRINCIPALS_PATH = "/v1/admin/machine-principals";
/**
* The console path of the CP resource grant routes. site-admin-api forwards it to
* `/v1/permissions/grants` on cp-auth-api. A machine resource grant uses this route with
* {@link createCpMachineResourceGrantRequestSchema}.
*/
const CP_MACHINE_RESOURCE_GRANTS_PATH = "/v1/admin/permissions/grants";
/** Thrown by a path builder of this module when an argument is outside its grammar. */
var CpMachineIdentityPathError = class extends Error {
	argument;
	/**
	* @param argument the name of the refused argument: `principalId`, `policyId`, `permission`,
	*   `kid` or `roleSlug`.
	*/
	constructor(argument) {
		super(`machine identity admin path: ${argument} is outside its grammar`);
		this.argument = argument;
		this.name = "CpMachineIdentityPathError";
	}
};
function requireLowerUuid(value, argument) {
	if (!LOWER_UUID_PATTERN.test(value)) throw new CpMachineIdentityPathError(argument);
	return value;
}
/**
* The path of one machine principal: `/v1/admin/machine-principals/<principalId>`.
*
* Callers: the console pane, for the detail read; the other builders of this module.
* Input: `principalId`, a lower-case UUID. Output: the path. Errors: any other string throws
* {@link CpMachineIdentityPathError} with `argument` `principalId`, so a caller can never build
* a path that names another route. No side effect.
*/
function cpMachinePrincipalPath(principalId) {
	return `${CP_MACHINE_PRINCIPALS_PATH}/${requireLowerUuid(principalId, "principalId")}`;
}
/**
* The disable path of one machine principal: `.../<principalId>/disable`.
*
* Input and errors: as {@link cpMachinePrincipalPath}. No side effect.
*/
function cpMachinePrincipalDisablePath(principalId) {
	return `${cpMachinePrincipalPath(principalId)}/disable`;
}
/**
* The trust policy collection of one machine principal: `.../<principalId>/trust-policies`. The
* list read and the create write use it.
*
* Input and errors: as {@link cpMachinePrincipalPath}. No side effect.
*/
function cpMachineTrustPoliciesPath(principalId) {
	return `${cpMachinePrincipalPath(principalId)}/trust-policies`;
}
/**
* The disable path of one trust policy: `.../<principalId>/trust-policies/<policyId>/disable`.
*
* Input: two lower-case UUIDs. Errors: a refused `principalId` throws first, with `argument`
* `principalId`; then a refused `policyId` throws with `argument` `policyId`. No side effect.
*/
function cpMachineTrustPolicyDisablePath(principalId, policyId) {
	return `${cpMachineTrustPoliciesPath(principalId)}/${requireLowerUuid(policyId, "policyId")}/disable`;
}
/**
* The token lifetime path of one trust policy: `.../<principalId>/trust-policies/<policyId>/token-ttl`
* (plan 2026-10-31-door-long-push, D4). The body is {@link setCpMachineTrustPolicyTokenTtlRequestSchema}.
*
* Input and errors: as {@link cpMachineTrustPolicyDisablePath}. No side effect.
*/
function cpMachineTrustPolicyTokenTtlPath(principalId, policyId) {
	return `${cpMachineTrustPoliciesPath(principalId)}/${requireLowerUuid(policyId, "policyId")}/token-ttl`;
}
/**
* The site grant collection of one machine principal: `.../<principalId>/site-grants`. The list
* read uses it.
*
* Input and errors: as {@link cpMachinePrincipalPath}. No side effect.
*/
function cpMachineSiteGrantsPath(principalId) {
	return `${cpMachinePrincipalPath(principalId)}/site-grants`;
}
/**
* The path of one site grant: `.../<principalId>/site-grants/<permission>`, with the permission
* percent-encoded by `encodeURIComponent` (each `:` becomes `%3A`). The `PUT` and `DELETE` routes
* use it.
*
* Input: a lower-case UUID and a member of {@link CP_MACHINE_SITE_PERMISSIONS}. Errors: a refused
* `principalId` throws first, with `argument` `principalId`; then a permission outside the list
* throws with `argument` `permission`. No side effect.
*/
function cpMachineSiteGrantPath(principalId, permission) {
	const base = cpMachineSiteGrantsPath(principalId);
	if (!isCpMachineSitePermission(permission)) throw new CpMachineIdentityPathError("permission");
	return `${base}/${encodeURIComponent(permission)}`;
}
/**
* The key collection of one machine principal: `.../<principalId>/keys`. The key list read and
* the key register write use it (plan 2026-10-28-identity-mcp-tools, I6).
*
* Input and errors: as {@link cpMachinePrincipalPath}. No side effect.
*/
function cpMachineKeysPath(principalId) {
	return `${cpMachinePrincipalPath(principalId)}/keys`;
}
/**
* The path of one registered key: `.../<principalId>/keys/<kid>`. The key revoke route uses it.
*
* Input: a lower-case UUID and a `kid` of {@link cpMachineKeyIdSchema}: 43 base64url characters,
* the canonical form of 32 bytes. A `kid` holds no `/`, `%` or `.`, so it needs no encoding.
* Errors: a refused `principalId` throws first, with `argument` `principalId`; then a refused
* `kid` throws with `argument` `kid`. No side effect.
*/
function cpMachineKeyPath(principalId, kid) {
	const base = cpMachineKeysPath(principalId);
	if (!cpMachineKeyIdSchema.safeParse(kid).success) throw new CpMachineIdentityPathError("kid");
	return `${base}/${kid}`;
}
/**
* The platform registry role collection of one machine principal:
* `.../<principalId>/platform-roles`. The grant write uses it (D4).
*
* Input and errors: as {@link cpMachinePrincipalPath}. No side effect.
*/
function cpMachinePlatformRolesPath(principalId) {
	return `${cpMachinePrincipalPath(principalId)}/platform-roles`;
}
/**
* The path of one platform registry role of a machine principal:
* `.../<principalId>/platform-roles/<roleSlug>`. The revoke route uses it (D4).
*
* Input: a lower-case UUID and a member of {@link CP_MACHINE_PLATFORM_ROLES}. A member holds only
* lower-case letters and `_`, so it needs no encoding. Errors: a refused `principalId` throws
* first, with `argument` `principalId`; then a role outside the list throws with `argument`
* `roleSlug`. No side effect.
*/
function cpMachinePlatformRolePath(principalId, roleSlug) {
	const base = cpMachinePlatformRolesPath(principalId);
	if (!isCpMachinePlatformRole(roleSlug)) throw new CpMachineIdentityPathError("roleSlug");
	return `${base}/${roleSlug}`;
}
/**
* A row id of `cp_auth`: a lower-case UUID of any version. An upper-case UUID, a UUID with braces
* and any other string is a `ZodError`. Postgres prints a `uuid` column in lower case, so every id
* the server answers passes.
*/
const cpMachineIdentityIdSchema = z.string().regex(LOWER_UUID_PATTERN, "a lower-case UUID");
/** An ISO 8601 UTC timestamp, as `Date.prototype.toISOString` writes it. */
const timestampSchema = z.iso.datetime();
/**
* The slug grammar of a machine principal, the same string as the CHECK
* `machine_principals_slug_grammar` of cp-auth-api: lower-case letters, digits and inner hyphens.
* It holds no `:`, so a slug can never spell a `svc:` or `machine:` subject.
*/
const CP_MACHINE_PRINCIPAL_SLUG_PATTERN = "^[a-z0-9][a-z0-9-]*[a-z0-9]$";
/** The smallest slug length, inclusive. */
const CP_MACHINE_PRINCIPAL_SLUG_MIN_LENGTH = 3;
/** The largest slug length, inclusive. */
const CP_MACHINE_PRINCIPAL_SLUG_MAX_LENGTH = 64;
/** The largest display name length, inclusive. The smallest is 1. */
const CP_MACHINE_PRINCIPAL_DISPLAY_NAME_MAX_LENGTH = 200;
/**
* A machine principal slug: {@link CP_MACHINE_PRINCIPAL_SLUG_PATTERN}, 3 to 64 characters. Any other
* string is a `ZodError`.
*/
const cpMachinePrincipalSlugSchema = z.string().min(3).max(64).regex(new RegExp(CP_MACHINE_PRINCIPAL_SLUG_PATTERN), "slug must match ^[a-z0-9][a-z0-9-]*[a-z0-9]$, with no colon, so it can never spell a subject");
const displayNameSchema = z.string().min(1).max(200);
/**
* The body of `POST /v1/admin/machine-principals`: `slug` and `displayName`, nothing else.
* The route makes a principal of kind `external` with no trust policy and no grant. A slug that
* exists answers 409; a slug of the door-pull form `dp-<id>-door-pull` answers 400 in the service.
*/
const createCpMachinePrincipalRequestSchema = z.object({
	slug: cpMachinePrincipalSlugSchema.describe("Stable, human-typeable name, for example \"zion-build-release-dp\""),
	displayName: displayNameSchema.describe("Free text for people; never parsed")
}).strict();
/**
* The path parameters of every route of one principal: `id`, a lower-case UUID. Strict.
*/
const cpMachinePrincipalParamSchema = z.object({ id: cpMachineIdentityIdSchema }).strict();
/**
* One machine principal, as the list, create and disable routes answer it.
*
* - `id`: the principal id. A minted token names it as `sub = machine:<id>`.
* - `slug`, `displayName`: as created; no route changes them.
* - `disabled`: true after the disable route. A disabled principal mints no new token. A token
*   that it minted before keeps its claims until `exp` (at most 900 s).
* - `createdBy`: the operator id of the creator, or null for a principal that a migration made or
*   whose creator left.
* - `createdAt`, `updatedAt`: ISO 8601 UTC.
*/
const cpMachinePrincipalSchema = z.object({
	id: cpMachineIdentityIdSchema,
	slug: cpMachinePrincipalSlugSchema,
	displayName: displayNameSchema,
	disabled: z.boolean(),
	createdBy: cpMachineIdentityIdSchema.nullable(),
	createdAt: timestampSchema,
	updatedAt: timestampSchema
}).strict();
/** The answer of `GET /v1/admin/machine-principals`: every principal, in the server's order. */
const cpMachinePrincipalListSchema = z.array(cpMachinePrincipalSchema);
/**
* The issuer of GitHub Actions ID tokens. A policy of a GitHub Actions issuer must require the
* claim {@link GITHUB_REQUIRED_CLAIM_KEY}. A GitHub Actions issuer is this string, or this string
* followed by a port or a path. GitHub Enterprise Cloud writes the path form
* `https://token.actions.githubusercontent.com/<enterprise>` into `iss`.
*
* At the S5 contract step, the CHECK `machine_trust_policies_github_requires_job_workflow_ref` and
* `createMachineTrustPolicySchema` of cp-auth-api compare the exact string alone. This contract is
* the stricter rule, and cp-auth-api must adopt it (WS-15b).
*/
const GITHUB_ACTIONS_ISSUER = "https://token.actions.githubusercontent.com";
/**
* Matches a GitHub Actions issuer: {@link GITHUB_ACTIONS_ISSUER} exact, or followed by `:` (a port)
* or `/` (a path). A host that only starts with the same text, such as
* `token.actions.githubusercontent.com.example`, does not match. The issuer shape refuses an
* upper-case host, so the literal is lower case.
*/
const GITHUB_ACTIONS_ISSUER_FAMILY_RE = /^https:\/\/token\.actions\.githubusercontent\.com(?:[:/]|$)/;
/** The claim a GitHub Actions policy must require: the workflow file and ref of the job. */
const GITHUB_REQUIRED_CLAIM_KEY = "job_workflow_ref";
/** The largest issuer length, inclusive. */
const CP_MACHINE_TRUST_POLICY_ISSUER_MAX_LENGTH = 253;
/** The largest subject length, inclusive. The smallest is 1. */
const CP_MACHINE_TRUST_POLICY_SUBJECT_MAX_LENGTH = 512;
/** The largest length of the JSON text of `requiredClaims`, inclusive. */
const CP_MACHINE_TRUST_POLICY_REQUIRED_CLAIMS_MAX_JSON_LENGTH = 1024;
/** The largest `requiredRefPrefix` length, inclusive. */
const CP_MACHINE_TRUST_POLICY_REF_PREFIX_MAX_LENGTH = 200;
/**
* The grammar of `requiredRefPrefix`: `refs/`, then one or more segments that each end with `/`.
* A segment starts with a letter, a digit, `_` or `-`, and holds letters, digits, `.`, `_` and `-`.
* So `refs/heads/release/` and `refs/tags/` pass, and `refs/`, `refs//`, `refs/heads`, a `*`, a
* `%`, white space, `..` as a segment start and a segment that starts with `.` are refused.
*/
const CP_MACHINE_TRUST_POLICY_REF_PREFIX_PATTERN = /^refs\/(?:[A-Za-z0-9_-][A-Za-z0-9._-]*\/)+$/;
/**
* An https issuer with a lower-case host of dot-joined labels, an optional port and an optional
* path. The same rule as `createMachineTrustPolicySchema` in cp-auth-api, which is stricter than the
* CHECK `machine_trust_policies_issuer_shape`: a host with a leading or a trailing dot, or an empty
* label, is refused, so a dotted spelling of a host can never equal the plain one.
*/
const ISSUER_SHAPE_RE = /^https:\/\/[a-z0-9-]+(?:\.[a-z0-9-]+)*(:[0-9]+)?(\/[A-Za-z0-9._~/-]*)?$/;
/** The wildcard characters of glob and SQL `LIKE`, and white space, which a subject must not hold. */
const SUBJECT_FORBIDDEN_RE = /[*%\s]/;
/**
* True when `v` holds a character a subject must not hold: `*`, `%`, white space, or a control
* character (U+0000 to U+001F and U+007F). A policy matches the asserted `sub` exactly. The control
* range is a code compare, not a regex class, so the package lint stays under its warning cap.
*/
function hasForbiddenSubjectCharacter(v) {
	if (SUBJECT_FORBIDDEN_RE.test(v)) return true;
	for (let i = 0; i < v.length; i += 1) {
		const code = v.charCodeAt(i);
		if (code < 32 || code === 127) return true;
	}
	return false;
}
/** The `submitter_kind` values a release-source scoped policy may require: `human` and `agent`. */
const SCOPED_SUBMITTER_KINDS = /* @__PURE__ */ new Set(["human", "agent"]);
/**
* The issuer of a trust policy, exact: {@link ISSUER_SHAPE_RE}, at most 253 characters, and no
* trailing `/`. Any other string is a `ZodError`.
*/
const cpMachineTrustPolicyIssuerSchema = z.string().max(253).regex(ISSUER_SHAPE_RE, "issuer must be an https URL with a lower-case host, for example \"https://token.actions.githubusercontent.com\"").refine((v) => !v.endsWith("/"), { message: "issuer must not have a trailing slash" });
/**
* The asserted `sub` a trust policy matches, exact: 1 to 512 characters, with no `*`, `%`, white
* space or control character. Any other string is a `ZodError`.
*/
const cpMachineTrustPolicySubjectSchema = z.string().min(1).max(512).refine((v) => !hasForbiddenSubjectCharacter(v), { message: "subject must not contain *, %, white space or control characters; a policy matches the asserted sub exactly" });
/**
* The required claims of a trust policy: a flat object with at least one key. Each key and each
* value is a non-empty string, and the JSON text of the object is at most 1024 characters. An
* empty object is a `ZodError`: the subject alone is too coarse a trust boundary.
*/
const cpMachineTrustPolicyRequiredClaimsSchema = z.record(z.string().min(1, "claim key must not be empty"), z.string().min(1, "claim value must not be empty")).refine((obj) => Object.keys(obj).length > 0, { message: "requiredClaims must be non-empty; the subject alone is too coarse a trust boundary" }).refine((obj) => JSON.stringify(obj).length <= CP_MACHINE_TRUST_POLICY_REQUIRED_CLAIMS_MAX_JSON_LENGTH, { message: "requiredClaims must serialize to at most 1024 characters" });
/**
* A ref prefix of a trust policy: {@link CP_MACHINE_TRUST_POLICY_REF_PREFIX_PATTERN}, at most 200
* characters. Any other string is a `ZodError`.
*/
const cpMachineTrustPolicyRefPrefixSchema = z.string().max(200).regex(CP_MACHINE_TRUST_POLICY_REF_PREFIX_PATTERN, "requiredRefPrefix must be refs/ and one or more segments, each ending with /");
/**
* The shortest token lifetime a trust policy may set, in seconds (plan 2026-10-31-door-long-push, D1).
* The longest is {@link FEDERATED_RELEASE_TOKEN_MAX_TTL_SECONDS}.
*/
const CP_MACHINE_TRUST_POLICY_TOKEN_TTL_MIN_SECONDS = 60;
/**
* The token lifetime of an unscoped trust policy, in whole seconds from
* {@link CP_MACHINE_TRUST_POLICY_TOKEN_TTL_MIN_SECONDS} to {@link FEDERATED_RELEASE_TOKEN_MAX_TTL_SECONDS}
* (plan 2026-10-31-door-long-push, D1). An unscoped exchange through a policy with a value mints a
* token of that lifetime, and never past the assertion's `run_deadline` (D2). A policy with no value
* mints `CP_FEDERATED_TOKEN_TTL`, at most 900 s.
*/
const cpMachineTrustPolicyTokenTtlSchema = z.number().int().min(60).max(FEDERATED_RELEASE_TOKEN_MAX_TTL_SECONDS);
/**
* The body of `POST /v1/admin/machine-principals/:id/trust-policies`.
*
* - `issuer`: {@link cpMachineTrustPolicyIssuerSchema}.
* - `subject`: {@link cpMachineTrustPolicySubjectSchema}.
* - `requiredClaims`: {@link cpMachineTrustPolicyRequiredClaimsSchema}. The exchange requires each
*   claim on the assertion with an equal value.
* - `releaseSourceScoped` (optional): true makes a release-source scoped policy. The exchange then
*   skips the subject compare, and the CP release sources name the one app the token may publish.
* - `requiredRefPrefix` (optional): {@link cpMachineTrustPolicyRefPrefixSchema}. The minter gives the
*   token no site permission unless the verified `ref` claim of the assertion starts with this
*   prefix and holds at least one more character (R-044 item 1). Absent means no ref rule.
* - `tokenTtlSeconds` (optional): {@link cpMachineTrustPolicyTokenTtlSchema}. Absent means the CP
*   default, `CP_FEDERATED_TOKEN_TTL` (at most 900 s). A release-source scoped policy refuses it, with the path
*   `tokenTtlSeconds` (plan 2026-10-31-door-long-push, D3).
*
* Refusals, each a `ZodError` with the path `requiredClaims`:
* - a GitHub Actions `issuer` with no {@link GITHUB_REQUIRED_CLAIM_KEY} claim. A GitHub Actions
*   issuer is {@link GITHUB_ACTIONS_ISSUER} exact, or followed by a port or a path, such as the
*   GitHub Enterprise Cloud form `https://token.actions.githubusercontent.com/<enterprise>`;
* - `releaseSourceScoped: true` with a `submitter_kind` claim that is absent or is not `human` or
*   `agent`.
*
* The service adds the rules no schema can see: a policy that equals, contains or overlaps an
* enabled policy answers 409; an unscoped policy with a `release-source:` subject answers 400
* (R-034); a principal that holds a site grant takes no policy on this plane's own issuer host.
*/
const createCpMachineTrustPolicyRequestSchema = z.object({
	issuer: cpMachineTrustPolicyIssuerSchema.describe("OIDC issuer, exact"),
	subject: cpMachineTrustPolicySubjectSchema.describe("The asserted sub this policy matches, exact"),
	requiredClaims: cpMachineTrustPolicyRequiredClaimsSchema.describe("Claims that must be present on the assertion and equal to these values"),
	releaseSourceScoped: z.boolean().optional().describe("true: a release-source scoped policy; the exchange does not compare the subject"),
	requiredRefPrefix: cpMachineTrustPolicyRefPrefixSchema.optional().describe("The verified ref claim must start with this prefix, or the token holds no site permission"),
	tokenTtlSeconds: cpMachineTrustPolicyTokenTtlSchema.optional().describe("The token lifetime of an unscoped policy, 60 to 3600 seconds; absent means CP_FEDERATED_TOKEN_TTL (at most 900)")
}).strict().refine((dto) => dto.tokenTtlSeconds === void 0 || dto.releaseSourceScoped !== true, {
	message: "a release-source scoped policy takes no tokenTtlSeconds; CP_FEDERATED_RELEASE_TOKEN_TTL bounds its tokens",
	path: ["tokenTtlSeconds"]
}).refine((dto) => !GITHUB_ACTIONS_ISSUER_FAMILY_RE.test(dto.issuer) || Object.hasOwn(dto.requiredClaims, "job_workflow_ref"), {
	message: `GitHub Actions policies must require requiredClaims.${GITHUB_REQUIRED_CLAIM_KEY}`,
	path: ["requiredClaims"]
}).refine((dto) => dto.releaseSourceScoped !== true || Object.hasOwn(dto.requiredClaims, "submitter_kind") && SCOPED_SUBMITTER_KINDS.has(dto.requiredClaims["submitter_kind"]), {
	message: `a release-source scoped policy must require ${SUBMITTER_KIND_CLAIM} = human or agent`,
	path: ["requiredClaims"]
});
/**
* The one ref root a trust policy prefix may start with on the create route: the release
* branches, `refs/heads/release/`. The D15 ruleset of the repository guards
* `refs/heads/release/**`. No ruleset guards another branch root, a tag root or `refs/pull/`,
* where GitHub writes the merge ref of a fork's pull request. So a prefix outside this root would
* give the principal's site permissions to code that any writer, or a fork, controls.
*
* The same string as `TRUSTED_REF_PREFIX_ROOT` of cp-auth-api, which the minter reads. Callers:
* {@link createCpMachineTrustPolicyRouteRequestSchema}, and cp-auth-api when it binds that schema.
*/
const CP_MACHINE_TRUST_POLICY_TRUSTED_REF_PREFIX_ROOT = "refs/heads/release/";
/** The bound of the CHECK `machine_trust_policies_required_claims_shape` on `length(required_claims::text)`. */
const REQUIRED_CLAIMS_DATABASE_TEXT_MAX_LENGTH = 1024;
/** The number of code points of `text`: the unit of Postgres `length()` on a UTF-8 database. */
function codePointLength(text) {
	return Array.from(text).length;
}
/**
* The length that Postgres `length(required_claims::text)` gives for `claims`, in code points.
*
* What it is for: the CHECK `machine_trust_policies_required_claims_shape` of cp-auth-api bounds
* that length at 1024. The `jsonb` text form is `{"k": "v", "k2": "v2"}`: one space after each `:`
* and after each `,`, which `JSON.stringify` does not write. Postgres escapes a string as
* `JSON.stringify` does for every character a claim can hold, and the key order of `jsonb` changes
* no length.
*
* Callers: {@link createCpMachineTrustPolicyRouteRequestSchema}; cp-auth-api and the identity MCP
* tools, which may print the figure.
* Input: a flat map of claim names to claim values, empty included. Output: 2 for an empty map;
* otherwise 2 for the braces, 2 per separator, and per entry the `JSON.stringify` text of the key
* and of the value in code points plus 2 for the colon and its space. So `{ a: 'b' }` gives 10.
* Pure; never throws.
*/
function cpMachineTrustPolicyRequiredClaimsDatabaseTextLength(claims) {
	const entries = Object.entries(claims);
	let length = 2 + 2 * Math.max(entries.length - 1, 0);
	for (const [key, value] of entries) length += codePointLength(JSON.stringify(key)) + 2 + codePointLength(JSON.stringify(value));
	return length;
}
/** U+0000, which a Postgres `jsonb` string cannot hold (SQLSTATE 22P05). */
const NUL = String.fromCharCode(0);
/**
* `true` when Postgres can store `text` as a `jsonb` string: it holds no U+0000 and no unpaired
* surrogate (`String.prototype.isWellFormed`). Pure; never throws.
*/
function isJsonbStorableText(text) {
	return text.isWellFormed() && !text.includes(NUL);
}
/**
* `true` when the namespace rule of {@link createCpMachineTrustPolicyRouteRequestSchema} admits
* `prefix`: {@link cpMachineTrustPolicyRefPrefixSchema} already refuses it, so the path keeps one
* issue, or it starts with {@link CP_MACHINE_TRUST_POLICY_TRUSTED_REF_PREFIX_ROOT}. Pure.
*/
function passesRefNamespaceRule(prefix) {
	return !cpMachineTrustPolicyRefPrefixSchema.safeParse(prefix).success || prefix.startsWith("refs/heads/release/");
}
/**
* `true` when no Git ref can start with `prefix`: it holds `..` or a segment that ends with `.lock`
* (`git check-ref-format` refuses both). Pure.
*/
function isGitDeadRefPrefix(prefix) {
	return prefix.includes("..") || prefix.includes(".lock/");
}
/**
* The body that `POST /v1/admin/machine-principals/:id/trust-policies` accepts: the exact rule of
* the cp-auth-api route (plan 2026-10-28-identity-mcp-tools, I6). It is
* {@link createCpMachineTrustPolicyRequestSchema} with four refinements, in this order:
*
* 1. a `requiredRefPrefix` that the prefix grammar admits must start with
*    {@link CP_MACHINE_TRUST_POLICY_TRUSTED_REF_PREFIX_ROOT}. So `refs/heads/`, `refs/tags/`, any
*    tag prefix, `refs/heads/main/` and `refs/pull/` are refused. Message: `requiredRefPrefix must
*    start with refs/heads/release/; the D15 ruleset guards the release branches alone, and a fork
*    writes refs/pull/`. Path `requiredRefPrefix`.
* 2. a `requiredRefPrefix` must hold no `..` and no segment that ends with `.lock`. Path
*    `requiredRefPrefix`.
* 3. `requiredClaims` must be at most 1024 code points in the database text form
*    ({@link cpMachineTrustPolicyRequiredClaimsDatabaseTextLength}). The base schema bounds the
*    `JSON.stringify` text, which is shorter by two characters per entry, less one. Path
*    `requiredClaims`.
* 4. no key and no value of `requiredClaims` may hold U+0000 or an unpaired surrogate, which a
*    `jsonb` string cannot store. Path `requiredClaims`.
*
* Who calls it: cp-auth-api binds it on the route; the identity MCP tool parses a body with it
* before it sends, so a tool never sends a body that the route answers with 400. The console keeps
* the base schema and reads the route's 400.
*
* zod 4 runs these refinements after a field issue that does not end the parse, such as a too
* long prefix. Rule 1 then adds no second issue on the path. It runs none of them after a type
* issue, such as a number in place of a string. No transform, so `createOpenApiDocument()` can
* render it. The service adds the rules no schema can see (409 for an overlap, and the refusals
* named on {@link createCpMachineTrustPolicyRequestSchema}).
*/
const createCpMachineTrustPolicyRouteRequestSchema = createCpMachineTrustPolicyRequestSchema.refine((dto) => dto.requiredRefPrefix === void 0 || passesRefNamespaceRule(dto.requiredRefPrefix), {
	message: "requiredRefPrefix must start with refs/heads/release/; the D15 ruleset guards the release branches alone, and a fork writes refs/pull/",
	path: ["requiredRefPrefix"]
}).refine((dto) => dto.requiredRefPrefix === void 0 || !isGitDeadRefPrefix(dto.requiredRefPrefix), {
	message: "requiredRefPrefix must not hold .. or a segment that ends with .lock; no Git ref can start with it",
	path: ["requiredRefPrefix"]
}).refine((dto) => cpMachineTrustPolicyRequiredClaimsDatabaseTextLength(dto.requiredClaims) <= REQUIRED_CLAIMS_DATABASE_TEXT_MAX_LENGTH, {
	message: "requiredClaims must be at most 1024 characters in the database text form, which adds a space after each : and each ,",
	path: ["requiredClaims"]
}).refine((dto) => Object.entries(dto.requiredClaims).every(([key, value]) => isJsonbStorableText(key) && isJsonbStorableText(value)), {
	message: "requiredClaims must not hold U+0000 or an unpaired surrogate; a jsonb string cannot store either",
	path: ["requiredClaims"]
});
/**
* The path parameters of the trust policy disable route: `id` and `policyId`, two lower-case UUIDs.
* Strict.
*/
const cpMachineTrustPolicyParamSchema = z.object({
	id: cpMachineIdentityIdSchema,
	policyId: cpMachineIdentityIdSchema
}).strict();
/**
* One trust policy, as the list, create, disable and token-ttl routes answer it.
*
* - `id`, `machinePrincipalId`: the policy and the principal it mints for.
* - `issuer`, `subject`, `requiredClaims`, `releaseSourceScoped`: as created.
* - `requiredRefPrefix`: the ref rule, or null for a policy with none (every policy made before
*   the column existed reads null).
* - `tokenTtlSeconds`: the token lifetime of the policy, or null for the CP default,
*   `CP_FEDERATED_TOKEN_TTL` (at most 900 s). An answer from a CP before plan 2026-10-31-door-long-push omits it, and the parse reads null.
* - `disabled`: true after the disable route. A disabled policy matches no new exchange.
* - `createdAt`, `updatedAt`: ISO 8601 UTC.
*
* The answer schema checks the stored shape and not the create refinements, so a row that an older
* rule admitted still parses.
*/
const cpMachineTrustPolicySchema = z.object({
	id: cpMachineIdentityIdSchema,
	machinePrincipalId: cpMachineIdentityIdSchema,
	issuer: cpMachineTrustPolicyIssuerSchema,
	subject: cpMachineTrustPolicySubjectSchema,
	requiredClaims: cpMachineTrustPolicyRequiredClaimsSchema,
	disabled: z.boolean(),
	releaseSourceScoped: z.boolean(),
	requiredRefPrefix: cpMachineTrustPolicyRefPrefixSchema.nullable(),
	tokenTtlSeconds: cpMachineTrustPolicyTokenTtlSchema.nullable().default(null),
	createdAt: timestampSchema,
	updatedAt: timestampSchema
}).strict();
/**
* The body of `POST /v1/admin/machine-principals/:id/trust-policies/:policyId/token-ttl` (plan
* 2026-10-31-door-long-push, D4). `tokenTtlSeconds` is {@link cpMachineTrustPolicyTokenTtlSchema},
* or null to clear the value and return the policy to the CP default,
* `CP_FEDERATED_TOKEN_TTL` (at most 900 s). Strict: no other key.
*
* The route answers the policy view. It refuses with 409 a disabled policy, a release-source scoped
* policy and a policy of a door-pull principal, and with 404 an unknown principal or policy.
*/
const setCpMachineTrustPolicyTokenTtlRequestSchema = z.object({ tokenTtlSeconds: cpMachineTrustPolicyTokenTtlSchema.nullable().describe("The token lifetime in seconds, 60 to 3600, or null for CP_FEDERATED_TOKEN_TTL (at most 900)") }).strict();
/** The answer of `GET .../:id/trust-policies`: every policy of the principal, disabled included. */
const cpMachineTrustPolicyListSchema = z.array(cpMachineTrustPolicySchema);
/**
* The answer of `GET /v1/admin/machine-principals/:id`: the principal view and every trust policy
* of the principal, disabled included. Strict.
*/
const cpMachinePrincipalDetailSchema = cpMachinePrincipalSchema.extend({ trustPolicies: cpMachineTrustPolicyListSchema }).strict();
/**
* The id of a registered key: its RFC 7638 SHA-256 thumbprint, which the server computes. 43
* base64url characters, the canonical unpadded form of 32 bytes: the last character carries no
* unused bits. It is `machineEnrollResponseSchema.shape.kid`, the one `kid` grammar of both planes.
* Any other string is a `ZodError`.
*/
const cpMachineKeyIdSchema = machineEnrollResponseSchema.shape.kid;
const CP_MACHINE_KEY_ALG_LIST = ["EdDSA", "ES256"];
/**
* The signature algorithms a registered key carries, the same list as the CHECK
* `principal_keys_alg_check`: `EdDSA` for an Ed25519 key and `ES256` for a P-256 key. Frozen.
*/
const CP_MACHINE_KEY_ALGS = Object.freeze([...CP_MACHINE_KEY_ALG_LIST]);
/** A member of {@link CP_MACHINE_KEY_ALGS}. Any other value is a `ZodError`. */
const cpMachineKeyAlgSchema = z.enum(CP_MACHINE_KEY_ALG_LIST);
const CP_MACHINE_KEY_STATUS_LIST = ["active", "revoked"];
/**
* The states of a registered key, the same list as the CHECK `principal_keys_status_check`:
* `active` verifies assertions; `revoked` verifies none, and the row stays. Frozen.
*/
const CP_MACHINE_KEY_STATUSES = Object.freeze([...CP_MACHINE_KEY_STATUS_LIST]);
/** A member of {@link CP_MACHINE_KEY_STATUSES}. Any other value is a `ZodError`. */
const cpMachineKeyStatusSchema = z.enum(CP_MACHINE_KEY_STATUS_LIST);
/**
* The body of `POST /v1/admin/machine-principals/:id/keys`: `publicJwk`, the public half of the key
* the machine signs its assertions with, and nothing else. `publicJwk` is `principalPublicJwkSchema`
* of `federated-token.ts`: an `OKP` Ed25519 or an `EC` P-256 public JWK. A private member `d`, a
* caller-chosen `kid` or any other member is a `ZodError`, so the body never carries a secret.
*
* The route answers 201 with `machineEnrollResponseSchema` (`principalId`, the server-computed
* `kid` and the registered-key `issuer`). It answers 400 for a JWK that does not import for its
* curve, 404 for an unknown principal, and 409 for a disabled principal, a `kid` that exists or a
* door-pull principal. The key verifies an exchange only once an enabled trust policy names its
* issuer. The machine keeps the private key; the CP never holds it, so no answer carries one.
*/
const registerCpMachineKeyRequestSchema = z.object({ publicJwk: principalPublicJwkSchema.describe("The public half of the key the machine signs its assertions with; Ed25519 or P-256") }).strict();
/**
* The path parameters of the key revoke route: `id`, a lower-case UUID, and `kid`, a
* {@link cpMachineKeyIdSchema}. Strict: any other key is a `ZodError`. A `kid` that is not
* canonical is refused before the route reads a row.
*/
const cpMachineKeyParamSchema = z.object({
	id: cpMachineIdentityIdSchema,
	kid: cpMachineKeyIdSchema
}).strict();
/**
* One registered key, as the key list and the key revoke routes answer it. The JWK itself is never
* returned. Strict.
*
* - `kid`: {@link cpMachineKeyIdSchema}.
* - `alg`: {@link cpMachineKeyAlgSchema}.
* - `status`: {@link cpMachineKeyStatusSchema}.
* - `createdAt`: ISO 8601 UTC.
* - `revokedAt`: ISO 8601 UTC of the revoke, or null while the key is active.
*
* Refusal: `status` and `revokedAt` must agree, as the CHECK `principal_keys_revoked_at_check`
* holds. An `active` key with a `revokedAt`, or a `revoked` key with none, is a `ZodError` on the
* path `revokedAt`.
*/
const cpMachineKeySchema = z.object({
	kid: cpMachineKeyIdSchema,
	alg: cpMachineKeyAlgSchema,
	status: cpMachineKeyStatusSchema,
	createdAt: timestampSchema,
	revokedAt: timestampSchema.nullable()
}).strict().refine((key) => key.status === "revoked" === (key.revokedAt !== null), {
	message: "revokedAt must be set for a revoked key and null for an active key",
	path: ["revokedAt"]
});
/**
* The answer of `GET .../:id/keys`: every key of the principal, revoked included, newest first.
* The `kid` is the primary key of `cp_auth.principal_keys`, so two rows with one `kid` are a
* `ZodError`. An empty list is valid: a principal that federates through an OIDC issuer holds no
* key.
*/
const cpMachineKeyListSchema = z.array(cpMachineKeySchema).refine((keys) => new Set(keys.map((key) => key.kid)).size === keys.length, { message: "keys must hold one row per kid" });
const CP_MACHINE_SITE_PERMISSION_LIST = [
	"cp:alert:silence",
	"cp:deployment-config:converge",
	"cp:deployment-config:read",
	"cp:platform-channel:pin",
	"cp:platform-channel:promote-version",
	"cp:platform-channel:read",
	"cp:platform:publish"
];
/**
* The closed list of site permissions a machine principal may hold through a row of
* `cp_auth.machine_site_grants`, in ascending code-unit order (R-044 item 1). Frozen.
*
* What each one opens for a release job (R-036, R-037):
* - `cp:alert:silence`: the alert silence create and delete routes, for the `cp` job;
* - `cp:deployment-config:converge` and `cp:deployment-config:read`: the two deployment-config
*   routes that `release-cp` calls, for the `cp` job;
* - `cp:platform-channel:pin`, `cp:platform-channel:promote-version` and `cp:platform-channel:read`:
*   the version register, deprecate and pin routes of release-engine, for the `dp` job;
* - `cp:platform:publish`: the SDK publish doors, for the three SDK jobs.
*
* Who reads it: cp-auth-api's site grant routes (the `permission` enum of
* {@link cpMachineSiteGrantParamSchema}), the CHECK `machine_site_grants_permission_check`, which
* restates it, and the minter, which drops a stored row outside it.
*
* Invariants, held by the test of this module: every member is in `ALL_CP_PERMISSIONS`; the list
* holds no `*`, no `cp:app:*` string, no `cp:permission:*`, `cp:role:*`, `cp:machine-*` or
* `cp:platform:admin` string. So a machine can never grant a right, widen its own trust, or act as
* an app. A change to this list is a change to what any machine may hold; it needs a ruling.
*/
const CP_MACHINE_SITE_PERMISSIONS = Object.freeze([...CP_MACHINE_SITE_PERMISSION_LIST]);
const CP_MACHINE_SITE_PERMISSION_SET = new Set(CP_MACHINE_SITE_PERMISSION_LIST);
/**
* Tells whether a value is a member of {@link CP_MACHINE_SITE_PERMISSIONS}.
*
* Callers: the minter, on each `permission` value it reads from `cp_auth.machine_site_grants`,
* before the value enters a token's `sitePermissions`; {@link cpMachineSiteGrantPath}.
* Input: any value. Output: `true` for a string equal to a member; `false` for every other string
* (`*`, the empty string, a case change, a member with a space, any other catalog string) and for
* every value that is not a string. It never throws and has no side effect.
*/
function isCpMachineSitePermission(value) {
	return typeof value === "string" && CP_MACHINE_SITE_PERMISSION_SET.has(value);
}
/** A member of {@link CP_MACHINE_SITE_PERMISSIONS}. Any other value is a `ZodError`. */
const cpMachineSitePermissionSchema = z.enum(CP_MACHINE_SITE_PERMISSION_LIST);
/**
* The path parameters of the site grant `PUT` and `DELETE` routes: `id`, a lower-case UUID, and
* `permission`, a member of {@link CP_MACHINE_SITE_PERMISSIONS} after URL decode. Strict.
*
* - `PUT` writes the row and answers it with 200. It is idempotent: a row that exists answers
*   unchanged, with its first `createdAt`. A principal that does not exist answers 404; a disabled
*   principal or a door-pull principal answers 409.
* - `DELETE` removes the row and answers 204 with no body. It is idempotent: a row that does not
*   exist answers 204. A principal that does not exist answers 404.
*/
const cpMachineSiteGrantParamSchema = z.object({
	id: cpMachineIdentityIdSchema,
	permission: cpMachineSitePermissionSchema
}).strict();
/**
* One site grant, as the list and `PUT` routes answer it: the principal, the permission and the
* ISO 8601 UTC time of the write that made the row. Strict.
*/
const cpMachineSiteGrantSchema = z.object({
	machinePrincipalId: cpMachineIdentityIdSchema,
	permission: cpMachineSitePermissionSchema,
	createdAt: timestampSchema
}).strict();
/**
* The answer of `GET .../:id/site-grants`: every site grant of the principal, in ascending order of
* `permission`, at most one row per member of {@link CP_MACHINE_SITE_PERMISSIONS}. Two rows with one
* permission, or rows out of order, are a `ZodError`.
*/
const cpMachineSiteGrantListSchema = z.array(cpMachineSiteGrantSchema).max(CP_MACHINE_SITE_PERMISSION_LIST.length).refine((rows) => rows.every((row, i) => i === 0 || rows[i - 1].permission < row.permission), { message: "site grants must be in ascending order of permission, one row per permission" });
/** The largest resource grant id length, inclusive; the bound of the site-admin-api proxy schema. */
const CP_MACHINE_RESOURCE_GRANT_ID_MAX_LENGTH = 256;
/** The largest role slug length, inclusive; the bound of the site-admin-api proxy schema. */
const CP_MACHINE_RESOURCE_GRANT_ROLE_SLUG_MAX_LENGTH = 120;
/**
* The body the console sends to `POST /v1/admin/permissions/grants` to give a machine principal a
* role on one resource. It is a narrow form of the route's own `createGrantSchema`: every body that
* passes here passes there.
*
* - `principalType`: `machine`, and no other value.
* - `principalId`: the machine principal id, a lower-case UUID.
* - `resourceType`: a member of `GRANT_RESOURCE_TYPES` (`app`, `tenant`, `dp`, `platform`).
* - `resourceId`: 1 to 256 characters. For `platform` it must be `PLATFORM_RESOURCE_ID`; the route
*   answers 400 for any other id.
* - `roleSlug`: 1 to 120 characters, for example `platform_publisher` for an image push job.
*
* The route adds its own refusals: 403 for a `platform` grant by a caller without
* `cp:platform:admin`, 400 for a role outside the platform list, 409 for a door-pull principal.
*/
const createCpMachineResourceGrantRequestSchema = z.object({
	principalType: z.literal("machine"),
	principalId: cpMachineIdentityIdSchema,
	resourceType: z.enum(GRANT_RESOURCE_TYPES),
	resourceId: z.string().min(1).max(256),
	roleSlug: z.string().min(1).max(120)
}).strict();
/**
* One resource grant row of a machine principal, as the grant routes answer it with the filter
* `principalType=machine`. Strict.
*
* - `id`: the grant id.
* - `principalType`: `machine`; `principalId`: the machine principal id.
* - `resourceType`, `resourceId`, `roleSlug`: as created.
* - `grantedAt`: ISO 8601 UTC; `grantedBy`: the operator id of the grantor, or null.
* - `revokedAt`: null while the grant is active. A revoke stamps it and the row stays.
* - `expiresAt`: ISO 8601 UTC, the end of a timed `dp` grant, or null for a grant with no end
*   (2026-10-27-dp-telemetry-residency, WS-5 D5). Optional, so a producer from before that plan
*   still parses.
* - `requestId`: the lower-case UUID of the DP access request behind a timed `dp` grant, or null.
*   Optional for the same reason.
*/
const cpMachineResourceGrantRowSchema = z.object({
	id: cpMachineIdentityIdSchema,
	principalType: z.literal("machine"),
	principalId: cpMachineIdentityIdSchema,
	resourceType: z.enum(GRANT_RESOURCE_TYPES),
	resourceId: z.string().min(1).max(256),
	roleSlug: z.string().min(1).max(120),
	grantedAt: timestampSchema,
	grantedBy: cpMachineIdentityIdSchema.nullable(),
	revokedAt: timestampSchema.nullable(),
	expiresAt: timestampSchema.nullable().optional(),
	requestId: cpMachineIdentityIdSchema.nullable().optional()
}).strict();
/** The answer of the grant list read with `principalType=machine&principalId=<id>`. */
const cpMachineResourceGrantListSchema = z.array(cpMachineResourceGrantRowSchema);
const CP_MACHINE_PLATFORM_ROLE_LIST = [
	"app_registry_reader",
	"platform_publisher",
	"platform_registry_reader"
];
/**
* The closed list of registry roles that the narrow platform role route gives a machine principal
* on the `platform` resource (plan 2026-10-28-identity-mcp-tools, D4), in ascending code-unit
* order. Frozen.
*
* - `app_registry_reader`: the read of every app image on the docker door (`cp:app:registry-read`).
*   cp-auth-api gives it to an external machine principal alone.
* - `platform_publisher`: a push to the platform registry paths (`cp:platform:publish`).
* - `platform_registry_reader`: a read of the platform registry (`cp:platform:registry-read`).
*
* These are the three seeded roles that the `platform` grant rules of cp-auth-api
* (`platform-grant-policy.ts`) admit. A custom role and `platform_admin` are outside the list, so
* the route never puts a CP-wide string on a machine. A change to this list is a change to what an
* MCP tool may give a machine; it needs a ruling.
*/
const CP_MACHINE_PLATFORM_ROLES = Object.freeze([...CP_MACHINE_PLATFORM_ROLE_LIST]);
const CP_MACHINE_PLATFORM_ROLE_SET = new Set(CP_MACHINE_PLATFORM_ROLE_LIST);
/**
* Tells whether a value is a member of {@link CP_MACHINE_PLATFORM_ROLES}.
*
* Callers: {@link cpMachinePlatformRolePath}; the identity MCP tools and cp-auth-api, before they
* name a role. Input: any value. Output: `true` for a string equal to a member; `false` for every
* other string (`platform_admin`, the empty string, a case change, a member with a space) and for
* every value that is not a string. It never throws and has no side effect.
*/
function isCpMachinePlatformRole(value) {
	return typeof value === "string" && CP_MACHINE_PLATFORM_ROLE_SET.has(value);
}
/** A member of {@link CP_MACHINE_PLATFORM_ROLES}. Any other value is a `ZodError`. */
const cpMachinePlatformRoleSchema = z.enum(CP_MACHINE_PLATFORM_ROLE_LIST);
/**
* The permission that the two platform role routes check with `@RequirePermission`:
* `cp:permission:grant`, the string that the generic grant routes check. It is already in the
* elevated-only scope set of cp-auth-api, so the routes add no scope string (D4, research option A,
* default F2). The route also reads the live `platform_admin` permissions of the caller in the
* write transaction, so `cp:platform:admin` decides at the commit.
*/
const CP_MACHINE_PLATFORM_ROLE_PERMISSION = "cp:permission:grant";
/**
* The body of `POST /v1/admin/machine-principals/:id/platform-roles`: `roleSlug`, a member of
* {@link CP_MACHINE_PLATFORM_ROLES}, and nothing else. Strict.
*
* The route gives the principal named by `:id` that role on the `platform` resource
* (`PLATFORM_RESOURCE_ID`), with principal type `machine`. The body names no resource type, no
* resource id and no principal type, so a caller cannot widen the grant. The route answers:
* - 201 with {@link cpMachinePlatformRoleGrantSchema}: a new grant, or a revoked grant of the same
*   tuple made active again. One audit row in the same transaction.
* - 400 for a body outside this schema.
* - 403 when the caller's live permissions, read in the write transaction, lack
*   `cp:platform:admin`.
* - 404 when no external machine principal has the id.
* - 409 when the grant is already active, or for a door-pull principal.
*/
const grantCpMachinePlatformRoleRequestSchema = z.object({ roleSlug: cpMachinePlatformRoleSchema.describe("The registry role, for example \"platform_publisher\" for an image push job") }).strict();
/**
* The path parameters of `DELETE /v1/admin/machine-principals/:id/platform-roles/:roleSlug`: `id`,
* a lower-case UUID, and `roleSlug`, a member of {@link CP_MACHINE_PLATFORM_ROLES}. Strict.
*
* The route revokes the active grant of that role on the `platform` resource and answers 204 with
* no body. It is idempotent: with no active grant it answers 204 and writes no audit row. It
* answers 403 as the grant route does, 404 when no external machine principal has the id, and 409
* for a door-pull principal. Live tokens of the principal keep the role until `exp`.
*/
const cpMachinePlatformRoleParamSchema = z.object({
	id: cpMachineIdentityIdSchema,
	roleSlug: cpMachinePlatformRoleSchema
}).strict();
/**
* One active platform registry role of a machine principal, as the grant route answers it: the
* row of {@link cpMachineResourceGrantRowSchema}, narrowed. Strict.
*
* - `resourceType`: `platform`; `resourceId`: `PLATFORM_RESOURCE_ID`.
* - `roleSlug`: a member of {@link CP_MACHINE_PLATFORM_ROLES}.
* - `revokedAt`: null, because the route answers an active grant.
*
* Every other field is as on {@link cpMachineResourceGrantRowSchema}. A row of another resource,
* another role or a revoked row is a `ZodError`, so a client never reads a wider grant as this one.
*/
const cpMachinePlatformRoleGrantSchema = cpMachineResourceGrantRowSchema.extend({
	resourceType: z.literal("platform"),
	resourceId: z.literal(PLATFORM_RESOURCE_ID),
	roleSlug: cpMachinePlatformRoleSchema,
	revokedAt: z.null(),
	expiresAt: z.null().optional(),
	requestId: z.null().optional()
}).strict();
//#endregion
export { CP_MACHINE_KEY_ALGS, CP_MACHINE_KEY_STATUSES, CP_MACHINE_PLATFORM_ROLES, CP_MACHINE_PLATFORM_ROLE_PERMISSION, CP_MACHINE_PRINCIPALS_PATH, CP_MACHINE_PRINCIPAL_DISPLAY_NAME_MAX_LENGTH, CP_MACHINE_PRINCIPAL_SLUG_MAX_LENGTH, CP_MACHINE_PRINCIPAL_SLUG_MIN_LENGTH, CP_MACHINE_PRINCIPAL_SLUG_PATTERN, CP_MACHINE_RESOURCE_GRANTS_PATH, CP_MACHINE_RESOURCE_GRANT_ID_MAX_LENGTH, CP_MACHINE_RESOURCE_GRANT_ROLE_SLUG_MAX_LENGTH, CP_MACHINE_SITE_PERMISSIONS, CP_MACHINE_TRUST_POLICY_ISSUER_MAX_LENGTH, CP_MACHINE_TRUST_POLICY_REF_PREFIX_MAX_LENGTH, CP_MACHINE_TRUST_POLICY_REF_PREFIX_PATTERN, CP_MACHINE_TRUST_POLICY_REQUIRED_CLAIMS_MAX_JSON_LENGTH, CP_MACHINE_TRUST_POLICY_SUBJECT_MAX_LENGTH, CP_MACHINE_TRUST_POLICY_TOKEN_TTL_MIN_SECONDS, CP_MACHINE_TRUST_POLICY_TRUSTED_REF_PREFIX_ROOT, CpMachineIdentityPathError, GITHUB_ACTIONS_ISSUER, GITHUB_REQUIRED_CLAIM_KEY, cpMachineIdentityIdSchema, cpMachineKeyAlgSchema, cpMachineKeyIdSchema, cpMachineKeyListSchema, cpMachineKeyParamSchema, cpMachineKeyPath, cpMachineKeySchema, cpMachineKeyStatusSchema, cpMachineKeysPath, cpMachinePlatformRoleGrantSchema, cpMachinePlatformRoleParamSchema, cpMachinePlatformRolePath, cpMachinePlatformRoleSchema, cpMachinePlatformRolesPath, cpMachinePrincipalDetailSchema, cpMachinePrincipalDisablePath, cpMachinePrincipalListSchema, cpMachinePrincipalParamSchema, cpMachinePrincipalPath, cpMachinePrincipalSchema, cpMachinePrincipalSlugSchema, cpMachineResourceGrantListSchema, cpMachineResourceGrantRowSchema, cpMachineSiteGrantListSchema, cpMachineSiteGrantParamSchema, cpMachineSiteGrantPath, cpMachineSiteGrantSchema, cpMachineSiteGrantsPath, cpMachineSitePermissionSchema, cpMachineTrustPoliciesPath, cpMachineTrustPolicyDisablePath, cpMachineTrustPolicyIssuerSchema, cpMachineTrustPolicyListSchema, cpMachineTrustPolicyParamSchema, cpMachineTrustPolicyRefPrefixSchema, cpMachineTrustPolicyRequiredClaimsDatabaseTextLength, cpMachineTrustPolicyRequiredClaimsSchema, cpMachineTrustPolicySchema, cpMachineTrustPolicySubjectSchema, cpMachineTrustPolicyTokenTtlPath, cpMachineTrustPolicyTokenTtlSchema, createCpMachinePrincipalRequestSchema, createCpMachineResourceGrantRequestSchema, createCpMachineTrustPolicyRequestSchema, createCpMachineTrustPolicyRouteRequestSchema, grantCpMachinePlatformRoleRequestSchema, isCpMachinePlatformRole, isCpMachineSitePermission, registerCpMachineKeyRequestSchema, setCpMachineTrustPolicyTokenTtlRequestSchema };

//# sourceMappingURL=machine-identity-admin.js.map